import React from "react";
import { useTranslation } from "react-i18next";
import { DragAndDrop, Draggable, DropBox } from "@common/dragAndDrop";
import { CrossIcon } from "@common/crossIcon";
import { ColumnIcon } from "../columnIcon";
import { AddColumn } from "./addColumn";
import { fixedColumns } from "./fixedColumns";
import { Props } from "./types";
import styles from "./styles.scss";

const DRAG_TYPE = "MOVE_DAILY_PLANNING_COLUMN";

function ColumnSelector({ project, template, columns, setColumns }: Props) {
  const { t, i18n } = useTranslation();
  const currentLang =
    ["fr", "en"].find((lang) => new RegExp(lang, "i").test(i18n.language)) ||
    "fr";

  const getName = (key: string) => {
    const criteria = template.find(({ uuid }) => uuid === key);
    return criteria
      ? criteria.labels[currentLang as keyof typeof criteria.labels] ||
          criteria.name
      : t(`wbs.columnNames.${key}`, key);
  };

  const moveColumn = (movedKey: string, toIndex: number) => {
    const fromIndex = columns.indexOf(movedKey);
    if (fromIndex === -1 || fromIndex === toIndex) return;
    const withoutMoved = columns.filter((key) => key !== movedKey);
    const insertAt = toIndex > fromIndex ? toIndex - 1 : toIndex;
    setColumns([
      ...withoutMoved.slice(0, insertAt),
      movedKey,
      ...withoutMoved.slice(insertAt),
    ]);
  };

  const knownColumns = columns.filter(
    (key) =>
      fixedColumns.includes(key) || template.find(({ uuid }) => uuid === key),
  );

  return (
    <DragAndDrop>
      <div className={styles.columns}>
        {knownColumns.map((key, index) => (
          <React.Fragment key={key}>
            <DropBox
              acceptedTypes={[DRAG_TYPE]}
              horizontalSlop={20}
              verticalSlop={20}
              drop={({ payload }) => moveColumn(payload, index)}
            >
              {({ payload }, canDrop) =>
                canDrop && (
                  <div className={styles.column}>
                    <ColumnIcon
                      criteriaKey={payload}
                      projectId={project.uuid}
                      className={styles.columnIcon}
                    />
                    <p>{getName(payload)}</p>
                  </div>
                )
              }
            </DropBox>
            <Draggable
              type={DRAG_TYPE}
              data={key}
              className={styles.columnWrapper}
              draggedClassName={styles.hidden}
            >
              <div className={styles.column}>
                <ColumnIcon
                  criteriaKey={key}
                  projectId={project.uuid}
                  className={styles.columnIcon}
                />
                <p>{getName(key)}</p>
                <div
                  className={styles.iconBox}
                  onClick={() => setColumns(columns.filter((k) => k !== key))}
                >
                  <CrossIcon className={styles.icon} />
                </div>
              </div>
            </Draggable>
          </React.Fragment>
        ))}
        <DropBox
          acceptedTypes={[DRAG_TYPE]}
          horizontalSlop={20}
          verticalSlop={20}
          drop={({ payload }) => moveColumn(payload, knownColumns.length)}
        >
          {({ payload }, canDrop) =>
            canDrop && (
              <div className={styles.column}>
                <ColumnIcon
                  criteriaKey={payload}
                  projectId={project.uuid}
                  className={styles.columnIcon}
                />
                <p>{getName(payload)}</p>
              </div>
            )
          }
        </DropBox>
        <div className={styles.columnWrapper}>
          <AddColumn
            project={project}
            template={template}
            columns={columns}
            setColumns={setColumns}
          />
        </div>
      </div>
    </DragAndDrop>
  );
}

export { ColumnSelector };
